import {
    bearingToUnitVector,
    degreesBetweenVectors as angleBetweenVectorsDegrees,
    dot,
} from "../Vector2d";

import { EventSystem } from "./EventSystem";
import { GeoLocation } from "./GeoLocation";
import { bearing, distanceInBetweenLocationsMeters, ILocation } from "./models/ILocation";
import {
    OrientationResult,
    registerOrientationListener,
    unregisterOrientationListener,
} from "./Orientation";

export type NavigationDirection = "left" | "right" | "straight";

export type NavigationDirectionChangedEvent = {
    direction: NavigationDirection;
    text: string;
    degreesOffTarget: number;
    distToTarget: number;
    targetHeading: number;
    orientation: OrientationResult;
    formattedDistToTarget: string;

    /**
     * [0-1] how close we are to the desired orientation.
     */
    orientationCloseness: number;
};

export class NavigationService {
    private _running = false;
    private _targetLocation: ILocation | undefined;
    private _orientationListenerId: string | undefined;
    private _orientation: OrientationResult | undefined;
    private _location: ILocation | undefined;

    constructor(
        private readonly _events: EventSystem,
        private readonly _geoLocation: GeoLocation,
        private _navigationSensitivity: number
    ) {
        this._events.subscribe("ds-location-changed", (location: ILocation) => {
            this._location = location;
            this._update();
        });

        this._events.subscribe(
            "navigation-sensitivity-changed",
            (sensitivity: number) => (this._navigationSensitivity = sensitivity)
        );

        this._events.subscribe("ds-orientation-changed", (orientation: OrientationResult) => {
            this._orientation = orientation;
            this._update();
        });
    }

    public start(location: ILocation) {
        if (this._running) {
            this._geoLocation.stop();
        }

        this._orientationListenerId = registerOrientationListener((result: OrientationResult) =>
            this._compassHandler(result)
        );
        this._geoLocation.start();
        this._targetLocation = location;
        this._running = true;
        this._events.raiseEvent("ds-navigation-started");
        this._update();
    }

    public stop() {
        this._geoLocation.stop();
        this._running = false;
        this._targetLocation = undefined;
        this._orientation = undefined;
        this._location = undefined;
        if (this._orientationListenerId) {
            unregisterOrientationListener(this._orientationListenerId);
            this._orientationListenerId = undefined;
        }
        this._events.raiseEvent("ds-navigation-stopped");
    }

    private _compassHandler(result: OrientationResult) {
        this._orientation = result;
        this._update();
    }

    private _update() {
        if (this._running && this._targetLocation && this._orientation && this._location) {
            if (this._targetLocation) {
                const targetHeading = bearing(this._location, this._targetLocation);

                // convert to vectors and compute the dot product to find the cos
                // of the angle between them
                const v1 = bearingToUnitVector(targetHeading);
                const v2 = bearingToUnitVector(this._orientation.heading);
                const degreesOffTarget = angleBetweenVectorsDegrees(v1, v2);

                // figure out direction instruction (turn left or right or continue straight)
                let direction: NavigationDirection | undefined;

                // check if we are going the right way
                if (degreesOffTarget < this._navigationSensitivity) {
                    direction = "straight";
                } else {
                    // not coincident so check which direction they turn
                    const cv = { x: -v1.y, y: v1.x };
                    const d = dot(v2, cv);
                    if (d < 0) {
                        direction = "left";
                    } else {
                        direction = "right";
                    }
                }

                const distToTarget = distanceInBetweenLocationsMeters(
                    this._targetLocation,
                    this._location
                );
                const formatted =
                    distToTarget > 1000
                        ? `${(distToTarget / 1000).toFixed(1)}km`
                        : `${distToTarget.toFixed(0)}m`;

                this._events.raiseEvent("ds-nav-direction-changed", {
                    direction,
                    text: `${direction} : ${formatted}`,
                    degreesOffTarget,
                    distToTarget,
                    targetHeading,
                    orientation: this._orientation,
                    formattedDistToTarget: formatted,
                    orientationCloseness: Math.abs(degreesOffTarget / 180.0),
                } as NavigationDirectionChangedEvent);
            }
        }
    }
}
